/**
 * Generic CRUD section for website content entities (testimonials, instructors, offers).
 * Renders a list of items with add/edit/delete + a modal form.
 * Each entity type provides its own field definitions.
 */
import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../lib/api.js';
import { useToast } from '../Toast.js';

export interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'url' | 'select' | 'toggle';
  placeholder?: string;
  required?: boolean;
  options?: Array<{ label: string; value: string }>;
}

interface CrudSectionProps {
  orgId: string;
  endpoint: string; // e.g., 'testimonials', 'instructors', 'offers'
  fields: FieldDef[];
  displayField: string; // which field to show as the item title
  subtitleField?: string; // which field to show as subtitle
}

export function CrudSection({ orgId, endpoint, fields, displayField, subtitleField }: CrudSectionProps) {
  const { toast } = useToast();
  const [items, setItems] = useState<Array<Record<string, unknown>>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await apiFetch<Record<string, Array<Record<string, unknown>>>>(`/orgs/${orgId}/${endpoint}`);
      setItems(res[endpoint] ?? []);
    } catch { /* ignore */ }
    finally { setIsLoading(false); }
  }, [orgId, endpoint]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = () => {
    setEditingId(null);
    setForm({});
    setShowModal(true);
  };

  const openEdit = (item: Record<string, unknown>) => {
    setEditingId(item.id as string);
    // Map camelCase DB fields to snake_case form fields
    const formData: Record<string, unknown> = {};
    for (const f of fields) {
      const camelKey = f.key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      formData[f.key] = item[camelKey] ?? item[f.key] ?? '';
    }
    setForm(formData);
    setShowModal(true);
  };

  const save = async () => {
    setIsSaving(true);
    try {
      if (editingId) {
        await apiFetch(`/orgs/${orgId}/${endpoint}/${editingId}`, { method: 'PUT', body: JSON.stringify(form) });
        toast('success', 'Updated');
      } else {
        await apiFetch(`/orgs/${orgId}/${endpoint}`, { method: 'POST', body: JSON.stringify(form) });
        toast('success', 'Added');
      }
      setShowModal(false);
      fetchItems();
    } catch { toast('error', 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  const remove = async (id: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/${endpoint}/${id}`, { method: 'DELETE' });
      toast('success', 'Removed');
      fetchItems();
    } catch { toast('error', 'Failed to remove'); }
  };

  if (isLoading) {
    return <div className="animate-pulse space-y-2">{[1, 2].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg" />)}</div>;
  }

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 italic">None added yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const title = (item[displayField.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())] ?? item[displayField] ?? 'Untitled') as string;
            const subtitle = subtitleField ? (item[subtitleField.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())] ?? item[subtitleField] ?? '') as string : '';
            return (
              <div key={item.id as string} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
                  {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
                </div>
                <button onClick={() => openEdit(item)} className="text-xs text-primary-600 hover:text-primary-800 px-2 py-1">Edit</button>
                <button onClick={() => remove(item.id as string)} className="text-xs text-red-400 hover:text-red-600 px-2 py-1">Remove</button>
              </div>
            );
          })}
        </div>
      )}

      <button onClick={openAdd}
        className="text-sm text-primary-600 hover:text-primary-800 font-medium">
        + Add {endpoint.slice(0, -1)}
      </button>

      {/* Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowModal(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto bg-white rounded-xl shadow-xl z-50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">
              {editingId ? 'Edit' : 'Add'} {endpoint.slice(0, -1)}
            </h3>
            {fields.map(f => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-gray-500 mb-1">{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea value={(form[f.key] as string) ?? ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={f.placeholder} rows={3}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-vertical" />
                ) : f.type === 'select' ? (
                  <select value={(form[f.key] as string) ?? ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <option value="">Select...</option>
                    {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : f.type === 'toggle' ? (
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={(form[f.key] as boolean) ?? true}
                      onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.checked }))}
                      className="rounded border-gray-300 text-primary-600" />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                ) : (
                  <input type={f.type === 'number' ? 'number' : 'text'} value={(form[f.key] as string) ?? ''}
                    onChange={e => setForm(prev => ({ ...prev, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value }))}
                    placeholder={f.placeholder}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                )}
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <button onClick={save} disabled={isSaving}
                className="flex-1 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {isSaving ? 'Saving...' : editingId ? 'Update' : 'Add'}
              </button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
